/**
 * User-bound realtime connection for activity changes, solve progress, and presence. Incoming full
 * records are deduplicated by ID and flushed once per animation frame; deleted IDs suppress late
 * records until the connection lifecycle resets.
 *
 * @module apps/web/src/hooks/useWebSocket
 */

import { useEffect, useRef, useCallback } from 'react';
import { useUserStore, type UserState } from '@/stores/userStore';
import { useTasksStore } from '@/stores/tasksStore';
import { terminalSolveStatus } from '@/lib/solveCompletion';
import { useScheduleStore, type SolveStatus } from '@/stores/scheduleStore';
import { taskToUI, type TaskUI } from '@fluxability/types';

/** Realtime envelope dispatched by message type. Payloads still require their operation-specific shape before they can update stores. */
interface WebSocketMessage {
  /** Realtime event discriminator used by the receive handler. */
  type: string;
  /** Event-specific data; each case of the receive handler reads it as that event's shape. */
  payload: unknown;
  /** Optional source-client identifier included by the realtime service. */
  senderId?: string;
  /** Optional server event timestamp retained with the message. */
  timestamp?: number;
}

/** Full activity record carried by activity events; only `id` is read before `taskToUI`. */
type RealtimeActivity = { /** Activity identifier. */ id?: string } | null | undefined;

/** Presence payload of `connected`, `user.joined` and `user.left`. */
interface PresencePayload {
  /** Everyone currently connected to the user's channel. */
  connectedUsers: UserState['connectedUsers'];
}

/** Payload of `task.updated` and `task.created`: a full record in one of three places, or a patch. */
interface ActivityEventPayload {
  /** Full record, current spelling. */
  activity?: RealtimeActivity;
  /** Full record, `task.created` legacy spelling. */
  task?: RealtimeActivity;
  /** Activity identifier when the payload itself is the full record. */
  id?: string;
  /** Activity identifier of a partial patch. */
  taskId?: string;
  /** Partial UI-shaped patch. */
  changes?: Partial<TaskUI>;
  /** Set on a reorder notice, which is currently ignored. */
  reordered?: boolean;
  /** Reordered activity identifiers. */
  task_ids?: string[];
}

/** Payload of `task.deleted`; any string identifier among the three fields is removed. */
interface DeletionPayload {
  /** Deleted activity, patch spelling. */
  taskId?: unknown;
  /** Deleted activity, record spelling. */
  id?: unknown;
  /** Every deleted activity of a cascade or batch. */
  deleted_ids?: unknown;
}

/**
 * Connect the current user with the fluxability.v1 protocol, cookie authentication, 30-second
 * heartbeats, and a 3-second reconnect delay. Applies the first record after a quiet frame at
 * once, batches the records that follow within that frame, and flushes them before solve
 * completion; deletions remove queued records and ghost blocks. Old
 * socket callbacks are ignored and timers/queues/subscriptions are cleaned up on user change or
 * unmount. Returns send, topic, presence, and task-update helpers.
 */
export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const userId = useUserStore(s => s.userId);
  const setConnected = useUserStore(s => s.setConnected);
  const setConnectedUsers = useUserStore(s => s.setConnectedUsers);
  const updateTask = useTasksStore(s => s.updateTask);
  const pendingActivities = useRef(new Map<string, TaskUI>());
  const activityFrame = useRef<number | null>(null);
  const removedActivities = useRef(new Set<string>());

  /** Apply the latest queued full record for each activity in one store update, then remove its superseded ghost placements. Flushing before terminal solve status makes accepted activity records visible before completion UI. */
  const flushActivities = useCallback(() => {
    if (activityFrame.current !== null) cancelAnimationFrame(activityFrame.current);
    activityFrame.current = null;
    const activities = [...pendingActivities.current.values()];
    pendingActivities.current.clear();
    if (activities.length) {
      useTasksStore.getState().upsertTasks(activities, 'realtime');
      const placed = new Set(activities.filter(task => task.scheduledStart).map(task => task.id));
      if (placed.size) {
        const schedule = useScheduleStore.getState();
        schedule.setBlocks(
          schedule.blocks.filter(block => !(block.isGhost && placed.has(block.taskId)))
        );
        schedule.setGhostBlocks(schedule.ghostBlocks.filter(block => !placed.has(block.taskId)));
      }
    }
  }, []);

  /**
   * Apply the first full record after a quiet frame at once, so a lone push renders before the
   * next frame instead of waiting for it; records that follow within that frame are coalesced by
   * ID and applied together at the frame. IDs deleted during this connection lifecycle cannot be
   * resurrected by a late queued record.
   */
  const queueActivity = useCallback(
    (value: RealtimeActivity) => {
      if (!value?.id || removedActivities.current.has(value.id)) return;
      pendingActivities.current.set(value.id, taskToUI(value));
      if (activityFrame.current !== null) return;
      flushActivities();
      activityFrame.current = requestAnimationFrame(() => {
        activityFrame.current = null;
        flushActivities();
      });
    },
    [flushActivities]
  );

  /** Dispatch accepted socket messages into presence, activity, and solve projections. Partial patches flush preceding full records first; deletion clears queued records and ghost placements. */
  const handleMessage = useCallback(
    (message: WebSocketMessage) => {
      // The realtime service is trusted to send each event's documented shape; the payload casts
      // below name that shape rather than validate it.
      switch (message.type) {
        case 'connected':
          setConnectedUsers((message.payload as PresencePayload).connectedUsers);
          break;

        case 'user.joined':
        case 'user.left':
          setConnectedUsers((message.payload as PresencePayload).connectedUsers);
          break;

        case 'energy.evidence.changed': {
          const payload = message.payload as { ownerId?: string };
          window.dispatchEvent(new CustomEvent('fluxability-energy-evidence-changed', { detail: payload }));
          break;
        }
        case 'task.updated': {
          const payload = message.payload as ActivityEventPayload;
          // Handle both format { taskId, changes } and full task format { id, ... }
          if (payload.activity?.id) {
            queueActivity(payload.activity);
          } else if (payload.taskId && payload.changes) {
            flushActivities();
            updateTask(payload.taskId, payload.changes);
          } else if (payload.id) {
            queueActivity(payload);
          } else if (payload.reordered && payload.task_ids) {
            // For now, reload tasks or ignore if UI optimistic update worked
          }
          break;
        }

        case 'task.created': {
          const payload = message.payload as ActivityEventPayload;
          queueActivity(payload.activity || payload.task || payload);
          break;
        }

        case 'task.deleted': {
          const payload = message.payload as DeletionPayload;
          const ids = new Set<string>(
            [
              payload.taskId,
              payload.id,
              ...(Array.isArray(payload.deleted_ids) ? payload.deleted_ids : []),
            ].filter((id): id is string => typeof id === 'string')
          );
          for (const id of ids) {
            pendingActivities.current.delete(id);
            removedActivities.current.add(id);
          }
          useTasksStore.getState().deleteTasks([...ids]);
          const schedule = useScheduleStore.getState();
          schedule.setBlocks(schedule.blocks.filter(block => !ids.has(block.taskId)));
          schedule.setGhostBlocks(schedule.ghostBlocks.filter(block => !ids.has(block.taskId)));
          break;
        }

        case 'solve.status': {
          const payload = message.payload as SolveStatus | null;
          // Flush preceding committed activities before reporting completion to the UI.
          if (terminalSolveStatus(payload?.status)) flushActivities();
          useScheduleStore.getState().setSolveStatus(payload);
          break;
        }

        case 'schedule.batch.updated': {
          const activities = (message.payload as { /** Committed activity records. */ activities?: unknown } | null)
            ?.activities;
          // Browser proposals publish their committed batch together, avoiding one frame per echo.
          if (Array.isArray(activities))
            for (const activity of activities) queueActivity(activity as RealtimeActivity);
          break;
        }

        case 'schedule.updated':
          break;

        case 'heartbeat.ack':
          // Heartbeat acknowledged
          break;

        default:
          console.log('Unknown message type:', message.type);
      }
    },
    [updateTask, queueActivity, flushActivities, setConnectedUsers]
  );

  /** Create the current user’s cookie-authenticated socket and guard callbacks by the active socket instance. Heartbeats start on open; only the current connection schedules reconnection after close. */
  const connect = useCallback(() => {
    if (!userId) return;

    const params = new URLSearchParams({ deviceId: crypto.randomUUID() });

    let wsUrl: string;
    if (import.meta.env.VITE_API_URL) {
      const url = new URL(import.meta.env.VITE_API_URL);
      const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${url.host}/ws/${encodeURIComponent(userId)}?${params.toString()}`;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/ws/${encodeURIComponent(userId)}?${params.toString()}`;
    }

    try {
      const ws = new WebSocket(wsUrl, 'fluxability.v1');
      wsRef.current = ws;

      ws.onopen = () => {
        if (wsRef.current !== ws) return;
        console.log('WebSocket connected');
        setConnected(true);

        // Start heartbeat
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'heartbeat' }));
          }
        }, 30000);
      };

      ws.onmessage = event => {
        if (wsRef.current !== ws) return;
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        console.log('WebSocket disconnected');
        setConnected(false);
        clearInterval(heartbeatIntervalRef.current);

        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = error => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [userId, setConnected, handleMessage]);

  /** Send only while the current socket is open; disconnected messages are dropped rather than retained for replay. */
  const send = useCallback((message: WebSocketMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  /** Request topic delivery on the current live connection; disconnected requests are not queued. */
  const subscribe = useCallback(
    (topics: string[]) => {
      send({ type: 'subscribe', payload: { topics } });
    },
    [send]
  );

  /** Request removal of topics from the current live connection. */
  const unsubscribe = useCallback(
    (topics: string[]) => {
      send({ type: 'unsubscribe', payload: { topics } });
    },
    [send]
  );

  /** Publish the current view and selected activity as transient presence, without persisting a planner mutation. */
  const updatePresence = useCallback(
    (view?: string, selectedTaskId?: string) => {
      send({
        type: 'presence',
        payload: { view, selectedTaskId },
      });
    },
    [send]
  );

  /** Send a versioned activity patch over the current socket; server acceptance still determines whether the update applies. */
  const broadcastTaskUpdate = useCallback(
    (taskId: string, changes: Record<string, unknown>, version: number) => {
      send({
        type: 'task.update',
        payload: { taskId, changes, version },
      });
    },
    [send]
  );

  useEffect(() => {
    // These refs hold one Map and one Set for the hook's lifetime; they are cleared, never replaced.
    const pending = pendingActivities.current;
    const removed = removedActivities.current;
    connect();
    // Optimistic local deletion can precede its realtime echo. Remove queued stale records now.
    const unsubscribeTasks = useTasksStore.subscribe((state, previous) => {
      if (state.tasks === previous.tasks || state.tasks.length >= previous.tasks.length) return;
      const currentIds = new Set(state.tasks.map(task => task.id));
      for (const task of previous.tasks)
        if (!currentIds.has(task.id)) {
          pending.delete(task.id);
          removed.add(task.id);
        }
    });

    return () => {
      unsubscribeTasks();
      if (activityFrame.current !== null) cancelAnimationFrame(activityFrame.current);
      activityFrame.current = null;
      pending.clear();
      removed.clear();
      useScheduleStore.getState().setSolveStatus(null);
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(heartbeatIntervalRef.current);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
      setConnected(false);
    };
  }, [connect, setConnected]);

  return {
    send,
    subscribe,
    unsubscribe,
    updatePresence,
    broadcastTaskUpdate,
  };
}
